import { useCallback, useMemo } from 'react';

import moment from 'moment';
import { TreeNode } from 'primereact/treenode';

import {
  CalendarAssignment,
  DepartmentSchedules,
  EmployeesSchedules,
  PersonAbsence,
  Shift,
  TaskForCalendarAssignment,
  TimelineItem,
  TimelineScale
} from '../../../types/interfaces';
import {
  buildEmployeeScheduleItems as buildEmployeeScheduleItemsHelper,
  buildTaskItemsMonthScale as buildTaskItemsMonthScaleHelper,
  buildTimesheetShiftItems as buildTimesheetShiftItemsHelper
} from '../builders';
import { EntityVisibility } from '../EntityVisibility';
import { buildGroups } from '../groupsBuilder';
import { computeTaskInterval as computeTaskIntervalHelper } from '../helpers';
import {
  buildTaskItems as buildTaskItemsHelper,
  computeTasksByDay as computeTasksByDayHelper
} from '../taskItemsBuilder';
import { buildTimelineItems } from '../timelineItemsBuilder';
import {
  CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT,
  CalendarAssignmentsDesktopLayout,
  CalendarViewMode,
  CustomTimelineItem
} from '../types';

type DeletedAbsenceKey = { id: number } | { guid: string };

export interface CalendarAssignmentsTimelineData {
  calendarAssignment: CalendarAssignment | null;
  departmentSchedules: DepartmentSchedules | null;
  employeesTasks: Record<string, TaskForCalendarAssignment[]>;
  employeesShifts: EmployeesSchedules | null;
  employeesAbsences: Record<string, Record<string, PersonAbsence[]>>;
  employeesTimesheet: Record<string, Record<string, number>>;
  employeesTotalTimesheet: Record<string, number>;
}

export interface CalendarAssignmentsTimelineChanges {
  newAbsences: PersonAbsence[];
  updatedAbsences: PersonAbsence[];
  deletedAbsenceKeys: DeletedAbsenceKey[];
  newShifts: Shift[];
  updatedShifts: Shift[];
  deletedShiftIds: string[];
  simulatedDeletedShifts: string[];
  simulatedUpdatedShifts: Shift[];
}

export interface CalendarAssignmentsTimelineViewState {
  startOfMonth: moment.Moment;
  timelineScale: TimelineScale;
  viewMode: CalendarViewMode;
  visibleEntities: EntityVisibility;
  visibleTimeStart: number;
  visibleTimeEnd: number;
  layout?: CalendarAssignmentsDesktopLayout;
}

export interface UseCalendarAssignmentsTimelineModelArgs {
  data: CalendarAssignmentsTimelineData;
  changes: CalendarAssignmentsTimelineChanges;
  view: CalendarAssignmentsTimelineViewState;
  copyToClipboard: (text: string, message: string) => void;
  selectedDepartmentFilter: TreeNode | null
}

export function useCalendarAssignmentsTimelineModel({
  data,
  changes,
  view,
  copyToClipboard,
  selectedDepartmentFilter
}: UseCalendarAssignmentsTimelineModelArgs) {
  const layout = view.layout ?? CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT;
  const showTotalColumn =
    view.viewMode === 'timesheet' && view.timelineScale !== TimelineScale.DAY;
  const sidebarWidth = showTotalColumn
    ? layout.sidebarInfoWidth + layout.totalColumnWidth
    : layout.sidebarInfoWidth;

  const computeTaskInterval = useCallback(computeTaskIntervalHelper, []);
  const computeTasksByDay = useMemo(
    () =>
      computeTasksByDayHelper({
        employeesTasks: data.employeesTasks,
        computeTaskInterval
      }),
    [data.employeesTasks, computeTaskInterval]
  );  
  const buildTaskItemsMonthScale = useCallback(() => {
    return buildTaskItemsMonthScaleHelper(
      view.visibleEntities.notClosedTask,
      view.visibleEntities.closedTask,
      computeTasksByDay,
      view.visibleTimeStart,
      view.visibleTimeEnd
    ) as CustomTimelineItem[];
  }, [
    computeTasksByDay,
    view.visibleEntities.closedTask,
    view.visibleEntities.notClosedTask,
    view.visibleTimeStart,
    view.visibleTimeEnd
  ]);

  const groups = useMemo(
    () =>
      buildGroups({
        calendarAssignment: data.calendarAssignment,
        employeesTotalTimesheet: data.employeesTotalTimesheet,
        showTotalColumn,
        sidebarWidth,
        sidebarInfoWidth: layout.sidebarInfoWidth,
        totalColumnWidth: layout.totalColumnWidth,
        DEPARTMENT_SCHEDULE_GROUP_ID: layout.departmentScheduleGroupId,
        copyToClipboard
      }),
    [
      copyToClipboard,
      data.calendarAssignment,
      data.employeesTotalTimesheet,
      layout.departmentScheduleGroupId,
      layout.sidebarInfoWidth,
      layout.totalColumnWidth,
      showTotalColumn,
      sidebarWidth
    ]
  );

  const buildEmployeeScheduleItems = useCallback(
    (
      employeesShifts: EmployeesSchedules | null,
      newShifts: Shift[],
      updatedShifts: Shift[],
      deletedShiftIds: string[],
      simulatedDeletedShifts: string[],
      simulatedUpdatedShifts: Shift[]
    ) => {
      return buildEmployeeScheduleItemsHelper({
        employeesShifts,
        newShifts,
        updatedShifts,
        deletedShiftIds,
        simulatedDeletedShifts,
        simulatedUpdatedShifts,
        employees: data.calendarAssignment?.employees,
        timelineScale: view.timelineScale
      });
    },
    [data.calendarAssignment?.employees, view.timelineScale]
  );

  const buildTimesheetShiftItems = useCallback(
    (employeesTimesheet: Record<string, Record<string, number>>) => {
      return buildTimesheetShiftItemsHelper(
        employeesTimesheet,
        view.timelineScale
      );
    },
    [view.timelineScale]
  );

  const taskItems: TimelineItem[] = useMemo(() => {
    return buildTaskItemsHelper({
      employeesTasks: data.employeesTasks,
      showNotClosed: view.visibleEntities.notClosedTask,
      showClosed: view.visibleEntities.closedTask
    });
  }, [
    data.employeesTasks,
    view.visibleEntities.closedTask,
    view.visibleEntities.notClosedTask
  ]);

  const items = useMemo(
    () =>
      buildTimelineItems({
        calendarAssignment: data.calendarAssignment,
        departmentSchedules: data.departmentSchedules,
        employeesShifts: data.employeesShifts,
        startOfMonth: view.startOfMonth,
        timelineScale: view.timelineScale,
        newShifts: changes.newShifts,
        updatedShifts: changes.updatedShifts,
        deletedShiftIds: changes.deletedShiftIds,
        simulatedDeletedShifts: changes.simulatedDeletedShifts,
        simulatedUpdatedShifts: changes.simulatedUpdatedShifts,
        viewMode: view.viewMode,
        employeesTimesheet: data.employeesTimesheet,
        visibleEntities: view.visibleEntities,
        employeesAbsences: data.employeesAbsences,
        newAbsences: changes.newAbsences,
        updatedAbsences: changes.updatedAbsences,
        deletedAbsenceKeys: changes.deletedAbsenceKeys,
        buildEmployeeScheduleItems,
        buildTimesheetShiftItems,
        buildTaskItemsMonthScale,
        taskItems,
        DEPARTMENT_SCHEDULE_GROUP_ID: layout.departmentScheduleGroupId,
        selectedDepartmentFilter
      }),
    [
      buildEmployeeScheduleItems,
      buildTaskItemsMonthScale,
      buildTimesheetShiftItems,
      changes.deletedAbsenceKeys,
      changes.deletedShiftIds,
      changes.newAbsences,
      changes.newShifts,
      changes.simulatedDeletedShifts,
      changes.simulatedUpdatedShifts,
      changes.updatedAbsences,
      changes.updatedShifts,
      data.calendarAssignment,
      data.departmentSchedules,
      data.employeesAbsences,
      data.employeesShifts,
      data.employeesTimesheet,
      layout.departmentScheduleGroupId,
      taskItems,
      view.startOfMonth,
      view.timelineScale,
      view.viewMode,
      view.visibleEntities,
      selectedDepartmentFilter
    ]
  );

  return {
    groups,
    items,
    sidebarWidth
  };
}
